// Spanish copy for the pricing pages. Same shape as copy.en.ts (the source of truth); keep the two
// in step. Plan names (Lite, Pro, Max, Pay as you go, Enterprise) are product names and stay
// in English, as they do in the app.

import type { PricingCopy } from "./copy.en";

const es: PricingCopy = {
  common: {
    perMonth: "/mes",
    onDemandFtpLink: "Cómo funciona el FTP bajo demanda",
    aMonth: "al mes",
    today: "Hoy",
    fromChangeDate: "Desde el 1 de noviembre",
    changeDate: "1 de noviembre de 2026",
    gb: "{n} GB",
    tb: "{n} TB",
    mostPopular: "El más elegido",
    renamedPlan: "{from} (ahora se llama {to})",
    planFeatures: {
      free: [
        {
          text: "Tu propia marca, “Powered by Photohawk”",
          note: "Tu logo y tus colores en cada galería. Una pequeña insignia “Powered by Photohawk” aparece al pie de tus galerías y de la página de inicio de sesión de tu espacio de trabajo. Pro y Max no la llevan.",
        },
        "Equipo de hasta 3 personas",
        "Soporte del centro de ayuda",
        {
          text: "Servidor FTP bajo demanda",
          note: "Si el FTP es imprescindible, inicia tu propio servidor desde la subida web, con tu propia dirección de Photohawk, que no cambia. Se apaga automáticamente tras 1 hora sin actividad.",
        },
        {
          text: "Subidas con vista previa primero",
          note: "La galería se publica con vistas previas ligeras en minutos mientras llegan los originales, tanto para ventas como para descargas gratuitas.",
        },
      ],
      pro: [
        {
          text: "Tu propia marca",
          note: "Tu logo y tus colores, sin la insignia de Photohawk.",
        },
        "Dominio propio",
        "Equipo de hasta 10 personas",
        "Analítica de visitantes",
        {
          text: "Servidor FTP bajo demanda",
          note: "Si el FTP es imprescindible, inicia tu propio servidor desde la subida web, con tu propia dirección de Photohawk, que no cambia. Se apaga automáticamente tras 1 hora sin actividad.",
        },
        {
          text: "Subidas con vista previa primero",
          note: "La galería se publica con vistas previas ligeras en minutos mientras llegan los originales, tanto para ventas como para descargas gratuitas.",
        },
        "Soporte por email",
      ],
      max: [
        "Todo lo de Pro",
        "Subida de vídeos",
        "Reparto de ingresos",
        "Varias marcas",
        "Miembros del equipo y roles ilimitados",
        "Analítica de visitantes",
        {
          text: "Servidor FTP bajo demanda",
          note: "Si el FTP es imprescindible, inicia tu propio servidor desde la subida web, con tu propia dirección de Photohawk, que no cambia. Se apaga automáticamente tras 1 hora sin actividad.",
        },
        {
          text: "Subidas con vista previa primero",
          note: "La galería se publica con vistas previas ligeras en minutos mientras llegan los originales, tanto para ventas como para descargas gratuitas.",
        },
        "Soporte prioritario",
      ],
    },
    planTaglines: {
      free: "Para empezar y para eventos ocasionales.",
      pro: "Para espacios de trabajo que venden con regularidad con su propia marca.",
      max: "Para estudios, equipos y gran volumen: la comisión más baja, más almacenamiento, vídeo y soporte prioritario.",
    },
    countries: {
      uk: "Reino Unido",
      my: "Malasia",
      ph: "Filipinas",
      mx: "México",
      co: "Colombia",
      eur: "Portugal y la zona euro",
      us: "Estados Unidos",
      ca: "Canadá",
    },
    inCountry: {
      uk: "en el Reino Unido",
      my: "en Malasia",
      ph: "en Filipinas",
      mx: "en México",
      co: "en Colombia",
      eur: "en Portugal y la zona euro",
      us: "en Estados Unidos",
      ca: "en Canadá",
    },
  },

  pricing: {
    seo: {
      title: "Precios | Photohawk",
      description:
        "Comisiones más bajas en todos los planes para fotógrafos de eventos: 16% en Lite, 14% en Pro y 12% en Max. Almacenamiento y descargas gratuitas incluidos.",
    },
    notice: "Nuevos precios desde el 1 de noviembre de 2026.",
    noticeLink: "Mira qué cambia",
    hero: {
      badge: "Precios",
      title: "Comisiones más bajas en todos los planes.",
      titleHighlight: "Desde solo {rate}.",
      subtitle:
        "Vende con un 16% en Lite, un 14% en Pro o un 12% en Max. Las comisiones por transacción funcionan igual que hoy.",
      events: "Los mismos planes para cualquier evento: carreras y marchas ciclistas, partidos, espectáculos, festivales, graduaciones y congresos.",
    },
    rate: {
      heading: "Tu comisión en cada plan",
      planLabel: "en {plan}",
      points: [
        "Las comisiones de tarjeta van aparte y funcionan igual que hoy.",
        "Nuestra comisión es la única tarifa de Photohawk en una venta.",
        "Los acuerdos personalizados actuales se mantienen hasta que terminen.",
      ],
    },
    plans: {
      heading: "Elige tu plan",
      subtitle:
        "Planes mensuales en dólares estadounidenses, sin permanencia. Cada plan superior baja tu comisión y suma almacenamiento y descargas gratuitas.",
      sellingLabel: "Venta",
      sellingLine: "{rate} de comisión sobre ventas",
      sellingNote: "Más comisiones por transacción, como hoy",
      freeDownloadsLabel: "Descargas gratuitas",
      freeDownloadsLine: "{count} fotos al mes incluidas",
      overageLine: "después, $8 por cada 1000 fotos",
      storageLabel: "Almacenamiento",
      storageLine: "{storage} incluidos",
      storageNote: "después, paquetes desde $5 al mes",
      includedLabel: "Incluye",
      cta: "Empezar",
      ctaFor: "con {plan}",
      planChange:
        "Cambio de plan: la comisión más baja de un plan se aplica desde tu primer mes completo en él. Los cambios a un plan inferior se aplican al final del mes.",
    },
    standard: {
      heading: "Incluido en todos los planes",
      subtitle: "Todos los planes lo tienen, también Lite, a $0 al mes.",
      items: [
        { id: "selfie", title: "Búsqueda por selfie", body: "Tus compradores se hacen un selfie y ven todas las fotos en las que aparecen." },
        { id: "bib", title: "Búsqueda por dorsal y hora", body: "Encuentra fotos por número de dorsal o por la hora en que se tomaron." },
        { id: "preview", title: "Protección de vistas previas", body: "Protección contra capturas de pantalla con toque para ver, para que las vistas previas vendan en lugar de copiarse." },
        {
          id: "uploader",
          title: "Subida web y Photohawk Studio",
          body: "La forma estándar de subir fotos: sube muchas fotos a la vez, se adapta a tu conexión y se reanuda sola. En todos los planes, primero pueden subir vistas previas ligeras para que tu galería esté en línea en minutos. Studio funciona hoy en tu navegador; la app Photohawk Studio para Mac, iPhone y iPad llega en noviembre de 2026.",
        },
        { id: "whatsapp", title: "Entrega por WhatsApp, un servicio de Photohawk para compradores", body: "Los compradores pueden pagar un pequeño extra al finalizar la compra para recibir sus fotos en WhatsApp, listas para compartir. Photohawk lo ofrece en todas las galerías: no tienes que configurar, fijar precios ni desactivar nada." },
      ],
    },
    storage: {
      heading: "Almacenamiento",
      subtitle:
        "Todos los planes incluyen almacenamiento. Si guardas más y tienes una tarjeta registrada, se añaden paquetes automáticamente: se te cobra la combinación más barata que cubra lo que guardas. ¿No tienes tarjeta registrada? Las nuevas subidas se pausan hasta que añadas una; no se borra nada y tus galerías siguen vendiendo.",
      includedHeading: "Incluido",
      packsHeading: "Paquetes de almacenamiento extra",
      packPrice: "${price} al mes",
      examplesHeading: "Cómo se suman los paquetes",
      example: "{plan} con {stored} guardados: {over} de más, se cobra como {mix} = ${price} al mes",
      note: "Te enviaremos un email cuando llegues al 80% de tu almacenamiento. Los paquetes se retiran cuando liberas espacio.",
    },
    freeDownloads: {
      heading: "Galerías de descarga gratuita",
      subtitle:
        "Comparte galerías que tus compradores descargan gratis. Cada plan incluye fotos cada mes; después son $8 por cada 1000 fotos ($0,008 por foto) en todos los planes.",
      line: "{count} fotos al mes",
      note: "El cupo se renueva cada mes y las galerías siguen funcionando por encima de él.",
    },
    example: {
      heading: "Lo que te queda",
      subtitle: "Un pedido en el Reino Unido pagado con tarjeta, en tu propia cuenta de Stripe, en cada plan. Prueba otro importe.",
      orderLabel: "Importe del pedido",
      order: "Pedido",
      cardFee: "Comisión de tarjeta de Stripe",
      cardFeeNote: "La cobra Stripe en tu cuenta, p. ej. tarjetas del Reino Unido 1,5% + 20p",
      colPlan: "Plan",
      colCommission: "Comisión de Photohawk",
      colKeep: "Te quedan aprox.",
      planCell: "{plan} ({rate})",
      note: "La comisión de Stripe depende de la tarjeta: las tarjetas internacionales y la conversión de divisa suelen costar más. Nuestra comisión es la única tarifa de Photohawk.",
    },
    cardFees: {
      heading: "Cómo funcionan las comisiones de tarjeta",
      intro: "Las comisiones de tarjeta y de procesamiento de pagos funcionan igual que hoy, aparte de nuestra comisión.",
      items: [
        {
          id: "stripe",
          title: "En tu propia cuenta de Stripe",
          body: "En el Reino Unido, la UE, Estados Unidos y otros países con Stripe, Stripe cobra su comisión estándar de procesamiento directamente en tu cuenta de Stripe.",
        },
        {
          id: "managed",
          title: "Donde Photohawk procesa el pago por ti",
          body: "En Malasia, Filipinas, México, Colombia, Ecuador y otros países donde los compradores pagan con monederos locales, se descuenta una comisión de procesamiento a tarifas locales basadas en el coste, como hoy.",
        },
      ],
      linkLabel: "Ver comisiones de tarjeta por país",
    },
    compare: {
      heading: "Compara todas las funciones",
      subtitle: "Todo lo que incluye Photohawk Studio, plan por plan. Studio ya funciona en tu navegador en todos los planes.",
      featureColumn: "Función",
      included: "Incluido",
      notIncluded: "No incluido",
      linkLabel: "Compara todas las funciones",
      sections: {
        selling: "Ventas",
        finding: "Encontrar fotos",
        galleries: "Galerías",
        brand: "Tu marca",
        insights: "Estadísticas",
        team: "Equipo",
        uploading: "Subidas",
        storage: "Almacenamiento",
        support: "Soporte",
      },
      rows: {
        commission: { label: "Comisión sobre las ventas", note: "Nuestra única tarifa en una venta. Las comisiones de tarjeta funcionan igual que hoy." },
        products: { label: "Descargas digitales, impresiones y productos", note: "Tus propios productos y precios." },
        packages: { label: "Paquetes y lotes", note: "" },
        coupons: { label: "Cupones", note: "" },
        preorder: { label: "Precio de preventa", note: "" },
        whatsapp: { label: "Entrega por WhatsApp (un servicio de Photohawk para compradores)", note: "Un extra opcional que el comprador paga al finalizar la compra para recibir sus fotos en WhatsApp, listas para compartir. Lo gestiona Photohawk en todas las galerías; no es una opción que tú controles." },
        payouts: { label: "Pagos", note: "A tu propia cuenta de Stripe, o pagados por Photohawk donde procesamos el pago por ti." },
        revenueSplit: { label: "Reparto de ingresos", note: "Comparte las ventas de una galería con fotógrafos, equipos, clubes o patrocinadores. Photohawk calcula cada parte y la paga por ti." },
        selfie: { label: "Búsqueda por selfie", note: "" },
        bib: { label: "Búsqueda por dorsal y por texto", note: "Encuentra fotos por número de dorsal o por el texto que aparece en la foto." },
        time: { label: "Búsqueda por hora", note: "" },
        find: { label: "Aparece en Encuentra tus fotos", note: "Los compradores encuentran tus galerías públicas en photohawk.com." },
        profiles: { label: "Búsquedas guardadas", note: "Guarda un selfie, un número o un texto y ve las nuevas coincidencias." },
        faceData: { label: "Datos faciales para la búsqueda por selfie", note: "Sin caducidad por defecto, o el plazo que elijas, desde el 1 de noviembre de 2026." },
        galleries: { label: "Galerías y colecciones", note: "Todas las que necesites." },
        private: { label: "Galerías privadas y contraseñas", note: "" },
        protection: { label: "Protección de vistas previas", note: "Protección contra capturas de pantalla con toque para ver." },
        watermarks: { label: "Marcas de agua", note: "" },
        freeDownloads: { label: "Fotos de descarga gratuita incluidas", note: "El cupo se renueva cada mes." },
        freeOverage: { label: "Fotos de descarga gratuita por encima del cupo", note: "Las galerías siguen funcionando." },
        video: { label: "Subida de vídeos", note: "" },
        address: { label: "Tu dirección de Photohawk", note: "tunombre.photohawk.com" },
        customDomain: { label: "Dominio propio", note: "Tus galerías en tu propia dirección web." },
        pages: { label: "Páginas", note: "Tus propias páginas junto a tus galerías." },
        emails: { label: "Plantillas de email personalizadas", note: "Edita los emails que reciben los compradores de cada galería." },
        logo: { label: "Tu logo y tus colores", note: "En tus galerías, páginas y emails." },
        noBadge: { label: "Sin la insignia “Powered by Photohawk”", note: "En Lite, una pequeña insignia aparece al pie de tus galerías y de tu página de inicio de sesión." },
        live: { label: "Visitantes en directo", note: "Cuántas personas están viendo una galería ahora mismo." },
        analytics: { label: "Analítica de visitantes", note: "Usuarios y visitas a lo largo del tiempo, galería por galería." },
        sales: { label: "Pedidos y ventas a lo largo del tiempo", note: "" },
        collectionDownloads: { label: "Descargas por colección", note: "" },
        people: { label: "Personas en tu espacio de trabajo", note: "Los colaboradores de galería también cuentan. Quienes ya están en tu espacio de trabajo mantienen su acceso." },
        roles: { label: "Roles", note: "Propietario, administrador, acceso solo para subir y acceso solo para ventas." },
        collaborators: { label: "Colaboradores de galería", note: "" },
        webUploader: { label: "Subida web", note: "Envía muchas fotos a la vez y se reanuda sola." },
        previewsFirst: { label: "Vista previa primero", note: "Las galerías se publican con vistas previas ligeras en minutos mientras llegan los originales." },
        soldFirst: { label: "Fotos vendidas primero", note: "Una foto comprada o descargada antes de que llegue su original va la siguiente." },
        originalsBy: { label: "Fecha de «Originales para el» para compradores", note: "Diles a los compradores cuándo estarán listos los originales." },
        ftp: { label: "Servidor FTP bajo demanda", note: "Si el FTP es imprescindible: tu propio servidor, iniciado desde la subida web. Se apaga automáticamente tras 1 hora sin actividad." },
        cloudImport: { label: "Importar desde Dropbox y Google Drive", note: "" },
        studioApp: { label: "App Photohawk Studio para Mac, iPhone y iPad", note: "Studio ya funciona en tu navegador." },
        included: { label: "Almacenamiento incluido", note: "" },
        packs: { label: "Almacenamiento adicional", note: "Se añade automáticamente con una tarjeta registrada, facturado como la combinación más barata." },
        you: { label: "Soporte para tu espacio de trabajo", note: "" },
        buyers: { label: "Photohawk atiende a tus compradores", note: "Las preguntas de los compradores llegan al soporte de Photohawk, salvo que configures tu propia dirección." },
      },
      values: {
        rate: "{rate}",
        storage: "{storage}",
        perMonth: "{n} al mes",
        upToPeople: "Hasta {n}",
        unlimited: "Ilimitado",
        upToDomains: "Hasta {n}",
        months: "{n} meses",
        fromNovember: "Desde noviembre de 2026",
        packs: "Desde ${usd} por {gb} GB al mes",
        overage: "${usd} por cada 1000",
        supportHelp: "Centro de ayuda",
        supportEmail: "Email",
        supportPriority: "Prioritario",
      },
    },
    faq: {
      heading: "Preguntas",
      items: [
        {
          q: "¿Están incluidas las comisiones de tarjeta?",
          a: "No. Las cobra el proveedor de pagos igual que hoy: Stripe en tu propia cuenta, o se descuentan a tarifas locales basadas en el coste donde Photohawk procesa el pago por ti. Nuestra comisión (16% en Lite, 14% en Pro, 12% en Max) es la única tarifa de Photohawk.",
        },
        {
          q: "¿Está incluida la búsqueda por selfie?",
          a: "Sí, en todos los planes, también Lite. Igual que la búsqueda por dorsal y hora, la protección de vistas previas y la subida web. La app Photohawk Studio para Mac, iPhone y iPad llegará a todos los planes cuando se lance en noviembre de 2026.",
        },
        {
          q: "¿Puedo cambiar de plan cuando quiera?",
          a: "Sí. Los planes son mensuales, sin contrato anual. Si pasas a un plan con una comisión más baja, esa comisión se aplica desde tu primer mes completo en él. Los cambios a un plan inferior se aplican al final del mes.",
        },
        {
          q: "Tengo un acuerdo personalizado. ¿Cambia?",
          a: "No. Los acuerdos personalizados actuales se mantienen hasta que terminen.",
        },
        {
          q: "¿Qué pasa con las descargas gratuitas por encima del cupo?",
          a: "Tus galerías de descarga gratuita siguen funcionando. Cada mes tu plan incluye 1000 fotos en Lite, 5000 en Pro y 10 000 en Max. Después son $8 por cada 1000 fotos ($0,008 por foto) en todos los planes.",
        },
        {
          q: "¿Y si necesito más almacenamiento?",
          a: "Lite incluye 150 GB, Pro 1 TB y Max 3 TB. Por encima, si tienes una tarjeta registrada, se añaden paquetes automáticamente: +100 GB por $5, +1 TB por $25 o +5 TB por $100 al mes, cobrados como la combinación más barata que cubra lo que guardas. Sin tarjeta registrada, las nuevas subidas se pausan hasta que añadas una: no se borra nada y tus galerías siguen vendiendo.",
        },
        {
          q: "¿Qué incluye Max?",
          a: "La comisión más baja, un 12%, además de 3 TB de almacenamiento, 10 000 fotos de descarga gratuita al mes, subida de vídeos, miembros del equipo ilimitados, analítica de visitantes y soporte prioritario.",
        },
        {
          q: "¿Cuántas personas puede haber en mi espacio de trabajo?",
          a: "Hasta 3 personas en Lite, hasta 10 en Pro y todas las que quieras en Max. ¿Ya tienes más personas en tu espacio de trabajo? Mantienen su acceso: el límite solo se aplica cuando invitas a alguien nuevo.",
        },
        {
          id: "ftp",
          q: "¿Puedo seguir subiendo por FTP?",
          a: "Sí, si el FTP es imprescindible. La subida web es ahora la forma estándar de subir fotos, en todos los planes: sube muchas fotos a la vez, muchas más que una app de FTP como FileZilla (2 a la vez por defecto, y como máximo 10), y se reanuda sola si se corta la conexión. Si aún necesitas FTP, cada persona de tu espacio de trabajo, en todos los planes, puede iniciar su propio servidor FTP bajo demanda desde la subida web, con una dirección personal de Photohawk que no cambia; se apaga automáticamente tras 1 hora sin actividad. Los servidores FTP compartidos cierran el 1 de noviembre de 2026.",
        },
        {
          q: "¿Photohawk Friends & Family forma parte de un plan?",
          a: "No. Friends & Family, nuestra app para las fotos de tu familia, es una app aparte, llegará pronto y no forma parte de ningún plan de Studio. ¿Gestionas tus galerías y subidas? Eso es Photohawk Studio: hoy funciona en tu navegador en todos los planes, y la app Studio para Mac, iPhone y iPad llega en noviembre de 2026.",
        },
      ],
    },
  },

  changes: {
    seo: {
      title: "Los precios cambian el 1 de noviembre de 2026 | Photohawk",
      description:
        "Desde el 1 de noviembre de 2026, la comisión baja en todos los planes de Photohawk, Pro y Max tienen más almacenamiento y las fotos de descarga gratuita vienen incluidas cada mes.",
    },
    hero: {
      badge: "Cambio de precios",
      title: "Los precios cambian el 1 de noviembre de 2026.",
      titleHighlight: "Comisión más baja en todos los planes.",
      subtitle:
        "La comisión de todos los planes baja, Pro y Max tienen más almacenamiento y las fotos de descarga gratuita ahora vienen incluidas cada mes. No tienes que hacer nada.",
      ctaPrimary: "Ver los nuevos precios",
      ctaSecondary: "Compara tu plan",
    },
    why: {
      heading: "Qué mejora",
      items: [
        {
          id: "rates",
          title: "Comisión más baja en todos los planes",
          body: "La comisión de todos los planes baja, entre 1,5 y 9 puntos. Pay as you go y Lite pasan a ser el nuevo Lite, sin cuota mensual y con un 16%, Pro baja al 14% y Enterprise, que ahora se llama Max, baja al 12%.",
        },
        {
          id: "storage",
          title: "Más almacenamiento en todos los planes",
          body: "Lite pasa de 100 GB a 150 GB, Pro de 500 GB a 1 TB y Max de 1 TB a 3 TB. Los 25 GB de Pay as you go pasan a ser 150 GB en Lite.",
        },
        {
          id: "downloads",
          title: "Fotos de descarga gratuita incluidas",
          body: "Hoy se cobra cada foto de una galería de descarga gratuita. Desde el 1 de noviembre, se incluyen de 1000 a 10 000 al mes y después son $8 por cada 1000: igual o menos que la tarifa actual en todos los planes.",
        },
        {
          id: "simple",
          title: "Precios más sencillos",
          body: "Tres planes en lugar de cuatro, una comisión por plan y almacenamiento extra en paquetes sencillos.",
        },
        {
          id: "order",
          title: "Las comisiones por transacción no cambian",
          body: "Las comisiones por transacción funcionan igual que hoy, aparte de nuestra comisión.",
        },
        {
          id: "deals",
          title: "Respetamos tu acuerdo",
          body: "Si tienes un acuerdo personalizado, se mantiene hasta que termine.",
        },
      ],
      honestHeading: "El único precio que sube",
      honestBody:
        "Enterprise, que ahora se llama Max, cuesta $49 al mes (antes $44,99) desde tu primera renovación a partir del 1 de noviembre. A cambio incluye 3 TB en lugar de 1 TB, 10 000 fotos de descarga gratuita al mes y la comisión más baja, un 12% en lugar de un 13,5%: en cuanto vendes unos $270 al mes, la comisión más baja cubre la diferencia de sobra. Pro baja de $29,99 a $29.",
    },
    when: {
      heading: "Qué pasa el 1 de noviembre",
      subtitle: "Una sola fecha para todos. Tu espacio de trabajo pasa a su nuevo plan automáticamente.",
      moves: {
        payg: "Sigue costando $0 al mes. Un 16% en lugar de un 25%, y 150 GB en lugar de 25 GB.",
        lite: "Lite se queda, y ahora es gratis: se acabaron los $16,99 al mes, un 16% en lugar de un 18,5%, y más almacenamiento: 150 GB en lugar de 100 GB. Si necesitas más, el almacenamiento extra cuesta desde $5 por cada 100 GB al mes. Tus galerías mantienen tu logo y tus colores, con una pequeña insignia “Powered by Photohawk”.",
        pro: "$29 en lugar de $29,99 al mes, un 14% en lugar de un 16%, y 1 TB en lugar de 500 GB.",
        enterprise: "Enterprise pasa a llamarse Max: el mismo plan con un nombre nuevo, ahora con 3 TB y una comisión del 12%. Cuesta $49 al mes en lugar de $44,99 desde tu primera renovación a partir del 1 de noviembre.",
      },
      points: [
        {
          id: "action",
          title: "No tienes que hacer nada",
          body: "Tu espacio de trabajo cambia el 1 de noviembre de 2026. Las galerías, los precios y los pagos siguen como siempre.",
        },
        {
          id: "month",
          title: "La regla del mes completo",
          body: "Si pasas a un plan con una comisión más baja, esa comisión se aplica desde tu primer mes completo en él. Los cambios a un plan inferior se aplican al final del mes.",
        },
        {
          id: "change",
          title: "Cambiar de plan",
          body: "Puedes cambiar de plan cuando quieras desde Facturación en tu espacio de trabajo.",
        },
        {
          id: "deals",
          title: "Acuerdos personalizados",
          body: "Los acuerdos personalizados actuales se mantienen hasta que terminen.",
        },
        {
          id: "privacy",
          title: "Búsqueda por selfie y privacidad",
          body: "Desde el 1 de noviembre, tú eliges cuánto tiempo se guardan los datos faciales usados para la búsqueda por selfie: sin caducidad por defecto, o un plazo después de subir cada foto. Tus fotos, tus ventas y la búsqueda por dorsal no se ven afectadas.",
        },
        {
          id: "team",
          title: "Tu equipo",
          body: "Lite incluye hasta 3 personas, Pro hasta 10 y Max ilimitadas. ¿Ya tienes más personas en tu espacio de trabajo? Mantienen su acceso: el límite solo se aplica al añadir a alguien nuevo.",
        },
      ],
    },
    ftp: {
      date: "1 de noviembre de 2026",
      heading: "La subida web es ahora la forma estándar de subir fotos",
      body: "Los servidores FTP compartidos siempre activos cierran el 1 de noviembre de 2026. La subida web es más rápida que el FTP y aprovecha al máximo tu conexión, en todos los planes. Si el FTP es imprescindible, puedes iniciar tu propio servidor bajo demanda.",
      items: [
        {
          id: "web",
          title: "La subida web, en todos los planes",
          body: "Sube muchas fotos a la vez, muchas más que una app de FTP como FileZilla (2 a la vez por defecto, y como máximo 10), y ajusta constantemente cuántas envía para aprovechar al máximo tu ancho de banda, y si se corta la conexión, se reanuda sola. En todos los planes, primero pueden subir vistas previas ligeras, así tu galería está en línea en minutos.",
        },
        {
          id: "ondemand",
          title: "Si el FTP es imprescindible: en todos los planes",
          body: "Ya disponible, en todos los planes: inicia tu propio servidor FTP bajo demanda desde la subida web, con una dirección personal de Photohawk que no cambia. Se apaga automáticamente tras 1 hora sin actividad.",
        },
        {
          id: "studio",
          title: "Photohawk Studio, en todos los planes",
          body: "La app de subida de Photohawk para tu espacio de trabajo, para escritorio e iOS.",
        },
      ],
    },
    privacyLink: "Política de conservación de datos biométricos",
    included: {
      heading: "Qué incluye cada plan",
      everyPlan:
        "En todos los planes: búsqueda por selfie, búsqueda por dorsal y por hora, protección de vistas previas, la subida web con vista previa primero y tu propio servidor FTP bajo demanda.",
      upgrades: "Pro y Max añaden analítica de visitantes y dominio propio. Max añade la subida de vídeos.",
    },
    compare: {
      heading: "Hoy frente a desde el 1 de noviembre",
      subtitle: "Plan por plan, en dólares estadounidenses.",
      rows: {
        fee: "Cuota mensual",
        commission: "Comisión sobre ventas",
        storage: "Almacenamiento incluido",
        downloads: "Fotos de descarga gratuita",
        extraStorage: "Almacenamiento extra",
      },
      downloadsToday: "{price} cada una",
      downloadsNext: "{count} al mes incluidas, después $8 por cada 1000",
      extraStorageToday: "$0,023 por GB al mes",
      extraStorageNext: "Paquetes: +100 GB $5, +1 TB $25, +5 TB $100 al mes",
    },
    countries: {
      heading: "Qué significa donde vendes",
      subtitle: "Ejemplos en tu moneda. Elige un país.",
      selectLabel: "País",
      examplesBadge: "Ejemplos",
      orderHeading: "Un pedido típico: {amount}",
      orderSubtitle: "El pedido medio {inCountry}, pagado con tarjeta. Comisión de tarjeta {fee}, {kind}.",
      kind: {
        stripe: "la cobra Stripe en tu cuenta",
        managed: "se descuenta donde Photohawk procesa el pago",
      },
      colPlan: "Tu plan",
      colCommission: "Comisión de Photohawk",
      colKeep: "Te queda",
      perOrderMore: "+{amount} por pedido",
      monthlyHeading: "Un mes de ventas, todo incluido",
      monthlySubtitle:
        "Cuota del plan, comisión y comisiones de tarjeta juntas, como parte de tus ventas, en el plan que menos cuesta en cada nivel.",
      level: "{amount} al mes en ventas",
      onPlan: "en {plan}",
      keepMore: "Te quedan unos {amount} más al mes",
      disclaimer:
        "Solo ejemplos, basados en el pedido medio y convertidos a tipos de cambio fijos (octubre de 2026). Tus cifras dependen de tus pedidos, tarjetas y plan. No incluyen cargos por descargas gratuitas ni por almacenamiento.",
    },
    faq: {
      heading: "Preguntas",
      items: [
        {
          q: "¿Tengo que hacer algo?",
          a: "No. El 1 de noviembre de 2026 tu espacio de trabajo pasa a su nuevo plan automáticamente. Las galerías, los precios y los pagos siguen como siempre.",
        },
        {
          q: "Estoy en Lite. ¿Qué pasa?",
          a: "Lite se queda, y ahora es gratis: desde el 1 de noviembre se acabaron los $16,99 al mes, tu comisión es un 16% en lugar de un 18,5%, y tienes más almacenamiento: 150 GB en lugar de 100 GB. Si guardas más, el almacenamiento extra cuesta desde $5 por cada 100 GB al mes y se añade automáticamente si tienes una tarjeta registrada (sin ella, las nuevas subidas se pausan hasta que añadas una; no se borra nada). Tus galerías mantienen tu logo y tus colores, con una pequeña insignia “Powered by Photohawk”.",
        },
        {
          q: "¿Y si guardo más de lo que incluye mi plan?",
          a: "Si tienes una tarjeta registrada, se añaden paquetes de almacenamiento automáticamente, desde $5 por cada 100 GB al mes, cobrados como la combinación más barata que cubra lo que guardas. Sin tarjeta registrada, las nuevas subidas se pausan hasta que añadas una: no se borra nada y tus galerías siguen vendiendo.",
        },
        {
          q: "Estoy en Enterprise. ¿Qué pasa?",
          a: "Enterprise pasa a llamarse Max: el mismo plan con un nombre nuevo. Desde el 1 de noviembre tu comisión es del 12% en lugar del 13,5% y tienes 3 TB en lugar de 1 TB. El nuevo precio, $49 al mes en lugar de $44,99, se aplica desde tu primera renovación a partir del 1 de noviembre.",
        },
        {
          q: "¿Cuántas personas puede haber en mi espacio de trabajo?",
          a: "Hasta 3 personas en Lite, hasta 10 en Pro y todas las que quieras en Max. ¿Ya tienes más personas en tu espacio de trabajo? Mantienen su acceso: el límite solo se aplica cuando invitas a alguien nuevo.",
        },
        {
          id: "ftp",
          q: "¿Qué pasa con el FTP?",
          a: "Los servidores FTP compartidos siempre activos cierran el 1 de noviembre de 2026, y la subida web es ahora la forma estándar de subir fotos, en todos los planes. Si el FTP es imprescindible, cada persona de tu espacio de trabajo, en todos los planes, ya puede iniciar su propio servidor FTP bajo demanda desde la subida web, con una dirección personal de Photohawk que no cambia; se apaga automáticamente tras 1 hora sin actividad.",
        },
        {
          q: "¿Cambia mi acuerdo personalizado?",
          a: "No. Los acuerdos personalizados actuales se mantienen hasta que terminen.",
        },
        {
          q: "Si cambio de plan, ¿cuándo empieza la nueva comisión?",
          a: "La comisión más baja de un plan se aplica desde tu primer mes completo en él. Los cambios a un plan inferior se aplican al final del mes.",
        },
        {
          q: "¿Cambian las comisiones de tarjeta?",
          a: "No. Las comisiones de tarjeta y de procesamiento de pagos funcionan igual que hoy, aparte de nuestra comisión.",
        },
        {
          q: "¿Photohawk Friends & Family forma parte de un plan?",
          a: "No. Friends & Family, nuestra app para las fotos de tu familia, es una app aparte, llegará pronto y no forma parte de ningún plan de Studio. ¿Gestionas tus galerías y subidas? Eso es Photohawk Studio: hoy funciona en tu navegador en todos los planes, y la app Studio para Mac, iPhone y iPad llega en noviembre de 2026.",
        },
      ],
    },
    cta: {
      heading: "Mira los nuevos precios completos",
      body: "Planes, almacenamiento, descargas gratuitas y comisiones de tarjeta por país, todo en una página.",
      button: "Ver los nuevos precios",
    },
  },

  app: {
    badge: "Próximamente",
    platform: "Primero en iPhone",
    heading: "Photohawk Friends & Family, para las fotos de tu familia",
    intro: "Friends & Family es nuestra app para todas las fotos de tu familia: las que haces y las que compartes. Es una app aparte, llegará pronto y no forma parte de los planes de Studio.",
    points: [
      { id: "private", title: "Encuentra las fotos de tu familia, en privado", body: "Ordena y encuentra fotos de las personas que quieres, directamente en tu teléfono." },
      { id: "albums", title: "Álbumes donde cada uno ve sus fotos", body: "Comparte un álbum y cada persona ve solo las fotos en las que aparece, gracias al selfie: la misma tecnología que las galerías de Photohawk." },
      { id: "event", title: "Reúne las fotos de todos en un evento", body: "Junta en un solo lugar las fotos que hicieron tus amigos y tu familia." },
      { id: "prints", title: "Copias impresas y fotolibros", body: "Convierte las mejores en copias impresas y fotolibros." },
    ],
    cta: "Avísame",
    ctaNote: "La lista de espera abre pronto.",
    phoneAlbum: "Solo yo",
    phoneAlbumCount: "12 fotos",
  },
};

export default es;
